import React from 'react';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useAppDispatch } from 'store/hooks';
import { useLocation, useParams } from 'react-router';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import { CountryCode } from 'libphonenumber-js';
import Grid from '@mui/material/Grid';
import { AxiosResponse } from 'axios';
import { Form, Formik, FormikHelpers } from 'formik';
import moment from 'moment';
import { useQuery } from 'src/hooks/useQuery';
import * as Yup from 'yup';
import { getIdentityLabel } from 'components/settings/model/Settings';
import { AgeVerification } from 'components/user/ageVerification';
import {
    clearSignupDataFromSession,
    getOnCloseRedirectUrl,
    getSignupDataFromSession,
    setSignupDataToSession
} from 'components/user/localAuth';
import {
    formatPhoneAsE164,
    getIdentityValidationScheme,
    SignUpDOBData,
    SignUpFormData
} from 'components/user/model/User';
import { CheckboxFormField } from 'lib/form/CheckboxFormField';
import { PasswordFormField } from 'lib/form/PasswordFormField';
import { TextFormField } from 'lib/form/TextFormField';
import { Link } from 'lib/Link';
import logger from 'lib/logger';
import { isDefined } from 'lib/typeInference';
import { useAuth } from 'lib/useAuth';
import { useLocalHistory } from 'lib/useLocalHistory';
import { clearErrors, register, setRegisterError } from 'store/auth/authSlice';
import { resetGuestState } from 'store/guest/guestSlice';
import { ApplicationState } from 'store/store';
import { LoadingButton } from 'ui/LoadingButton';
import { ROUTES } from 'pages/routes';
import { GoogleSSO } from 'components/user/GoogleSSO';
import { AppleSSO } from 'components/user/AppleSSO';
import { TermsAndConditions } from 'components/user/TermsAndCondition';
import { CountryCodePicker, getDefaultCountry } from 'components/user/CountryCodePicker';

const PREFIX = 'SignUpForm';

const classes = {
    root: `${PREFIX}root`,
    button: `${PREFIX}button`,
    forgot: `${PREFIX}forgot`,
    orContainer: `${PREFIX}orContainer`,
    or: `${PREFIX}or`
};

const Root = styled('div', {
    shouldForwardProp: prop => prop !== 'noPadding'
})<{ noPadding?: boolean }>(({ theme, noPadding }) => ({
    [`& .${classes.root}`]: {
        padding: noPadding ? 0 : theme.spacing(2)
    },
    [`& .${classes.button}`]: {
        height: '48px',
        fontSize: '1.2em'
    },
    [`& .${classes.forgot}`]: {
        margin: theme.spacing(0, 1.75),
        marginTop: theme.spacing(1),
        display: 'block'
    },
    [`& .${classes.orContainer}`]: {
        position: 'relative',
        marginBottom: theme.spacing(2),
        marginTop: theme.spacing(2)
    },
    [`& .${classes.or}`]: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: theme.palette.background.default,
        padding: theme.spacing(1, 3),
        color: theme.palette.divider
    }
}));

const TitleTypography = styled(Typography)(({ theme }) => ({
    color: theme.palette.titleTextColour,
    fontWeight: 600,
    fontSize: '1rem',
    padding: theme.spacing(1, 0, 2, 0)
}));

const birthDateInitialValues: SignUpDOBData = {
    birthDate: ''
};

/** Discriminated union returned by the `register` thunk — either the user is created (`success`)
 *  or activation is pending and a verification code has been dispatched (`pending`). */
export type RegisterResult =
    | { type: 'success'; id: string; user: import('components/user/model/User').User }
    | { type: 'pending'; id: string; user?: undefined };

export interface SignUpFormProps {
    /** Pre-fill the identity field (e.g. email captured upstream by the receipt flow). */
    defaultIdentity?: string;
    /**
     * Called after register completes successfully. When provided, the default behaviour of
     * navigating to `ROUTES.USER.ACTIVATE` is suppressed — the parent decides what to do
     * (e.g. render activation inline, dismiss the form).
     */
    onSuccess?: (result: RegisterResult) => void;
    /** Hide the "Already have an account? Sign in" link. */
    hideSignInLink?: boolean;
    /** Hide the trailing Terms & Conditions block. */
    hideTermsAndConditions?: boolean;
    /** Drop the outer Grid padding — useful when the form is embedded in a tight container. */
    noPadding?: boolean;
    /** If provided, the "Already have an account? Sign in" link calls this instead of
     *  navigating to `/login`. Useful when the parent renders the form inline and wants
     *  to swap to a sign-in form in the same surface. */
    onSwitchToLogIn?: () => void;
    /** Override the form's identity field type. By default, both-auth merchants render a phone
     *  field (the `pickOne` behaviour in `getIdentityLabel`). Set this when the parent has
     *  context — e.g. the receipt flow knows the user gave an email, so it passes `'email'`. */
    preferIdentityType?: 'email' | 'phone';
}

export const SignUpForm: React.FC<SignUpFormProps> = ({
    defaultIdentity,
    onSuccess,
    hideSignInLink = false,
    hideTermsAndConditions = false,
    noPadding = false,
    onSwitchToLogIn,
    preferIdentityType
}) => {
    const { t } = useTranslation();
    const { state: locationState } = useLocation();

    const dispatch = useAppDispatch();
    const queryIdentity = useQuery('identity');
    const paramFirstName = useQuery('firstName');
    const paramLastName = useQuery('lastName');
    const { getParsedPath, push } = useLocalHistory();
    const [isAgePageOpen, setAgePageOpen] = React.useState(false);
    const [signUpData, setSignUpData] = React.useState<SignUpFormData>();
    const [SSOInProgress, setSSOInProgress] = React.useState(false);
    const { merchantId } = useParams<{ merchantId: string }>();
    const { registerError, returnUrl } = useSelector((state: ApplicationState) => state.auth);
    const { isLoggedIn } = useAuth();
    const { settings } = useSelector((state: ApplicationState) => state.settings);
    const identityLabel = React.useMemo(() => {
        // Both-auth merchants normally default to phone (`pickOne=true` behaviour). If the
        // parent explicitly prefers a type AND the backend supports it, honour that instead.
        if (preferIdentityType === 'email' && settings?.emailAuthEnabled) {
            return 'email';
        }
        if (preferIdentityType === 'phone' && settings?.phoneAuthEnabled) {
            return 'phone';
        }
        return getIdentityLabel(settings?.emailAuthEnabled, settings?.phoneAuthEnabled, true);
    }, [preferIdentityType, settings?.emailAuthEnabled, settings?.phoneAuthEnabled]);
    // Parent is responsible for passing a `defaultIdentity` matching the form's identity type
    // (signal via `preferIdentityType`). Used as-is — no type-based filter here.
    const paramIdentity = defaultIdentity ?? queryIdentity ?? undefined;
    const [selectedCountry, setSelectedCountry] = React.useState<string>(() =>
        getDefaultCountry(
            settings?.phoneAuth?.supportedCountryCallingCodes ?? [],
            settings?.region?.phoneNumberCode
        )
    );
    const initialFields: SignUpFormData = React.useMemo(() => {
        if (!!signUpData) {
            return {
                identity: (paramIdentity || signUpData.identity) ?? '',
                firstName: (paramFirstName || signUpData.firstName) ?? '',
                lastName: (paramLastName || signUpData.lastName) ?? '',
                password: signUpData.password ?? '',
                confirmPassword: signUpData.confirmPassword ?? '',
                hasAgreedToShareData: signUpData.hasAgreedToShareData ?? false,
                hasAgreedToReceiveMarketing: signUpData.hasAgreedToReceiveMarketing ?? false
            };
        }
        return {
            identity: paramIdentity || locationState?.identity || '',
            firstName: paramFirstName || locationState?.user?.firstName || '',
            lastName: paramLastName || locationState?.user?.lastName || '',
            password: '',
            confirmPassword: '',
            hasAgreedToShareData: locationState?.user?.hasAgreedToShareData ?? false,
            hasAgreedToReceiveMarketing: locationState?.user?.hasAgreedToReceiveMarketing ?? false
        };
    }, [
        signUpData,
        paramIdentity,
        locationState?.identity,
        locationState?.user?.firstName,
        locationState?.user?.lastName,
        locationState?.user?.hasAgreedToShareData,
        locationState?.user?.hasAgreedToReceiveMarketing,
        paramFirstName,
        paramLastName
    ]);
    const shouldApplyAgeRequest = React.useMemo(
        () =>
            !!settings &&
            !!settings.app &&
            settings.app.ageRequestEnabled &&
            isDefined(settings.app.registrationMinimumAge),
        [settings]
    );
    const showCountryInfo = identityLabel === 'phone';

    const signupValidation = React.useMemo(
        () =>
            Yup.object().shape({
                identity: getIdentityValidationScheme(identityLabel, selectedCountry as CountryCode, t),
                firstName: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                lastName: Yup.string().required(t('FORMS_VALIDATION_REQUIRED')),
                password: Yup.string()
                    .min(8, t('FORMS_VALIDATION_MIN', { count: 8 }))
                    .required(t('FORMS_VALIDATION_REQUIRED')),
                confirmPassword: Yup.string()
                    .oneOf([Yup.ref('password')], t('ERROR_SIGN_UP_REPEAT_PASSWORD'))
                    .required(t('FORMS_VALIDATION_REQUIRED')),
                hasAgreedToShareData: Yup.boolean()
                    .required(t('FORMS_VALIDATION_REQUIRED'))
                    .equals([true], t('FORMS_VALIDATION_REQUIRED'))
            }),
        [identityLabel, t, selectedCountry]
    );

    const birthDateValidation = React.useMemo(
        () =>
            Yup.object().shape({
                birthDate: Yup.string()
                    .required(t('FORMS_VALIDATION_REQUIRED'))
                    .test(
                        'isAgeValid',
                        t('ONBOARDING_DOB_MESSAGE'),
                        (val: string) =>
                            !(
                                !!settings &&
                                !!settings.app &&
                                moment(val)
                                    .add(settings.app.registrationMinimumAge, 'years')
                                    .isAfter(moment(new Date()))
                            )
                    )
            }),
        [settings, t]
    );

    const handleKeyPress = React.useCallback(
        (submit: () => Promise<void>) => (e: React.KeyboardEvent) => {
            if (e.keyCode === 13) {
                submit();
            }
        },
        []
    );
    const handleRegisterSuccess = React.useCallback(
        (res: RegisterResult) => {
            if (onSuccess) {
                onSuccess(res);
                return;
            }
            push(ROUTES.USER.ACTIVATE, {}, '', { user: res });
        },
        [onSuccess, push]
    );
    const handleSubmit = React.useCallback(
        (data: SignUpFormData, { setSubmitting }: FormikHelpers<SignUpFormData>) => {
            const normalizedData: SignUpFormData = {
                ...data,
                identity:
                    identityLabel === 'phone'
                        ? formatPhoneAsE164(data.identity, selectedCountry as CountryCode)
                        : data.identity
            };
            if (shouldApplyAgeRequest) {
                const { firstName, lastName, identity, hasAgreedToShareData, hasAgreedToReceiveMarketing } =
                    normalizedData;
                setSignUpData(normalizedData);
                if (merchantId) {
                    setSignupDataToSession(merchantId, {
                        firstName,
                        lastName,
                        identity,
                        hasAgreedToShareData,
                        hasAgreedToReceiveMarketing
                    });
                }
                setAgePageOpen(true);
                setSubmitting(false);
                return;
            }
            if (merchantId) {
                clearSignupDataFromSession(merchantId);
            }
            dispatch(register(normalizedData))
                .unwrap()
                .then(handleRegisterSuccess)
                .catch(err => {
                    logger.warn(err);
                })
                .finally(() => {
                    setSubmitting(false);
                });
        },
        [dispatch, handleRegisterSuccess, identityLabel, merchantId, selectedCountry, shouldApplyAgeRequest]
    );
    const handleDOBSubmit = React.useCallback(
        (dobValues: SignUpDOBData, helpers: FormikHelpers<SignUpDOBData>) => {
            if (!!signUpData) {
                if (merchantId) {
                    clearSignupDataFromSession(merchantId);
                }
                dispatch(register({ ...signUpData, birthdate: dobValues.birthDate }))
                    .unwrap()
                    .then(handleRegisterSuccess)
                    .catch(err => {
                        logger.warn(err);
                    })
                    .finally(() => {
                        helpers.setSubmitting(false);
                    });
            }
        },
        [dispatch, handleRegisterSuccess, merchantId, signUpData]
    );

    const handleSSOSuccess = React.useCallback(() => {
        setSSOInProgress(false);
        if (merchantId) {
            clearSignupDataFromSession(merchantId);
        }
    }, [merchantId]);

    const handleSSOError = React.useCallback(
        (e: AxiosResponse<{ message: string }>) => {
            setSSOInProgress(false);
            dispatch(setRegisterError(e.data.message));
        },
        [dispatch]
    );

    const handleSSOStart = React.useCallback(() => {
        setSSOInProgress(true);
    }, []);

    const handleBackFromAgeVerification = React.useCallback(() => {
        setAgePageOpen(false);
    }, []);

    const handleCountryChange = React.useCallback(
        (setFieldValue: FormikHelpers<SignUpFormData>['setFieldValue']) => (value: string) => {
            setSelectedCountry(value);
            setFieldValue('identity', '');
        },
        []
    );
    const handleSignInLinkClick = React.useCallback(
        (e: React.MouseEvent) => {
            if (onSwitchToLogIn) {
                e.preventDefault();
                onSwitchToLogIn();
            }
        },
        [onSwitchToLogIn]
    );
    // Default post-login navigation: if onSuccess prop wasn't provided, fall through to the
    // pre-existing return-URL navigation. Skipped when onSuccess is provided so inline
    // consumers can render their own follow-on UI without being unmounted.
    React.useEffect(() => {
        if (onSuccess) {
            return;
        }
        if (isLoggedIn && !SSOInProgress && merchantId) {
            const onCloseReturnUrl = getOnCloseRedirectUrl(merchantId);
            const returnAddress = returnUrl ?? onCloseReturnUrl;
            if (returnAddress) {
                const [path, search] = returnAddress.split('?');
                push(path, {}, search);
            } else {
                push(ROUTES.BASE);
            }
        }
    }, [SSOInProgress, isLoggedIn, merchantId, onSuccess, push, returnUrl]);

    React.useEffect(() => {
        dispatch(resetGuestState());
        return () => {
            dispatch(clearErrors());
        };
    }, [dispatch]);
    React.useEffect(() => {
        if (!merchantId) {
            return;
        }
        const signupDataFromSessionAsString = getSignupDataFromSession(merchantId);
        if (!!signupDataFromSessionAsString) {
            const { firstName, lastName, identity, hasAgreedToShareData, hasAgreedToReceiveMarketing } =
                JSON.parse(signupDataFromSessionAsString);
            setSignUpData(oldData => ({
                firstName,
                lastName,
                identity,
                hasAgreedToShareData,
                hasAgreedToReceiveMarketing,
                password: oldData?.password ?? '',
                confirmPassword: oldData?.confirmPassword ?? ''
            }));
        }
    }, [merchantId]);

    return (
        <Root noPadding={noPadding}>
            {isAgePageOpen ? (
                <AgeVerification
                    error={registerError}
                    onBack={handleBackFromAgeVerification}
                    initialValues={birthDateInitialValues}
                    birthDateValidation={birthDateValidation}
                    onSubmit={handleDOBSubmit}
                />
            ) : (
                <Formik
                    initialValues={initialFields}
                    validationSchema={signupValidation}
                    onSubmit={handleSubmit}
                    enableReinitialize
                >
                    {({
                        submitForm,
                        setFieldValue,
                        isSubmitting,
                        isValid,
                        values: { hasAgreedToShareData }
                    }) => {
                        const countryPicker = (
                            <CountryCodePicker
                                selectedCountry={selectedCountry}
                                onCountryChange={handleCountryChange(setFieldValue)}
                                supportedCodes={settings?.phoneAuth?.supportedCountryCallingCodes ?? []}
                            />
                        );

                        return (
                            <Form className={classes.root}>
                                <Grid container spacing={2}>
                                    {!hideSignInLink && (
                                        <Grid size={12}>
                                            <Typography align="center">
                                                {t('AUTH_ALREADY_HAVE_ACCOUNT') + ' '}
                                                <Link
                                                    to={getParsedPath(ROUTES.USER.LOGIN)}
                                                    onClick={handleSignInLinkClick}
                                                >
                                                    {t('ONBOARDING_SIGN_IN')}
                                                </Link>
                                            </Typography>
                                        </Grid>
                                    )}
                                    {(settings?.googleSSOEnabled || settings?.appleSSOEnabled) && (
                                        <Grid size={12}>
                                            {/* Wrapping row: SSO buttons sit side-by-side when the form
                                                is wide enough for both at their minimum width, and stack
                                                otherwise (mobile, dialogs, the inline column). */}
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    flexWrap: 'wrap',
                                                    gap: 2,
                                                    justifyContent: 'center'
                                                }}
                                            >
                                                <GoogleSSO
                                                    onSignInError={handleSSOError}
                                                    onSignInSuccess={handleSSOSuccess}
                                                    onStart={handleSSOStart}
                                                />
                                                <AppleSSO
                                                    onSignInError={handleSSOError}
                                                    onSignInSuccess={handleSSOSuccess}
                                                />
                                            </Box>
                                        </Grid>
                                    )}
                                    {(settings?.appleSSOEnabled || settings?.googleSSOEnabled) && (
                                        <Grid size={12}>
                                            <Box className={classes.orContainer}>
                                                <Divider />
                                                <Typography className={classes.or}>OR</Typography>
                                            </Box>
                                        </Grid>
                                    )}
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextFormField
                                            name="firstName"
                                            label={t('ONBOARDING_SIGNUP_FIRSTNAME')}
                                            trimWhiteSpaces="sides"
                                        />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextFormField
                                            name="lastName"
                                            label={t('ONBOARDING_SIGNUP_LASTNAME')}
                                            trimWhiteSpaces="sides"
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TextFormField
                                            name="identity"
                                            type={identityLabel}
                                            label={t(
                                                `ONBOARDING_SIGNUP_LABEL_${identityLabel.toUpperCase()}`
                                            )}
                                            trimWhiteSpaces="sides"
                                            clearable
                                            clearAriaLabel="Clear"
                                            InputProps={{
                                                autoComplete: 'email',
                                                ...(showCountryInfo && {
                                                    startAdornment: countryPicker
                                                })
                                            }}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <PasswordFormField
                                            name="password"
                                            label={t('ONBOARDING_SIGNIN_PASSWORD')}
                                            onKeyDown={handleKeyPress(submitForm)}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <PasswordFormField
                                            name="confirmPassword"
                                            label={t('SIGN_UP_REPEAT_PASSWORD')}
                                            onKeyDown={handleKeyPress(submitForm)}
                                        />
                                    </Grid>
                                    <Grid size={12}>
                                        <TitleTypography align="center" variant="body1">
                                            {t('GDPR_PAGE_TITLE')}
                                        </TitleTypography>
                                        <Typography>
                                            {t('GDPR_BODY', {
                                                applicationName: settings?.app?.bundleDisplayName || ''
                                            })}
                                        </Typography>
                                    </Grid>
                                    <Grid size={12}>
                                        {(settings?.app?.registrationShowMarketingOptIn ?? true) && (
                                            <Box paddingLeft={2}>
                                                <CheckboxFormField
                                                    name="hasAgreedToReceiveMarketing"
                                                    label={t('GDPR_MARKETING_CHECKBOX_TITLE')}
                                                />
                                            </Box>
                                        )}
                                        <Box paddingLeft={2}>
                                            <CheckboxFormField
                                                name="hasAgreedToShareData"
                                                label={t('GDPR_DATA_CHECKBOX_TITLE')}
                                                color="primary"
                                            />
                                        </Box>
                                    </Grid>
                                    {!!registerError && (
                                        <Grid size={12}>
                                            <Typography color="error">{registerError}</Typography>
                                        </Grid>
                                    )}
                                    <Grid size={12}>
                                        {!hasAgreedToShareData && (
                                            <Tooltip title={t('AUTH_FORM_SIGNUP_TIP') || ''}>
                                                <div>
                                                    <LoadingButton
                                                        variant="contained"
                                                        color="primary"
                                                        disabled={true}
                                                        fullWidth
                                                        onClick={submitForm}
                                                        className={classes.button}
                                                        loading={isSubmitting}
                                                    >
                                                        {t('AUTH_FORM_SIGNUP')}
                                                    </LoadingButton>
                                                </div>
                                            </Tooltip>
                                        )}
                                        {hasAgreedToShareData && (
                                            <LoadingButton
                                                variant="contained"
                                                color="primary"
                                                disabled={!isValid || isSubmitting || SSOInProgress}
                                                fullWidth
                                                onClick={submitForm}
                                                className={classes.button}
                                                loading={isSubmitting || SSOInProgress}
                                            >
                                                {t('AUTH_FORM_SIGNUP')}
                                            </LoadingButton>
                                        )}
                                    </Grid>
                                    {!hideTermsAndConditions && (
                                        <Grid size={12}>
                                            <TermsAndConditions variant="caption" />
                                        </Grid>
                                    )}
                                </Grid>
                            </Form>
                        );
                    }}
                </Formik>
            )}
        </Root>
    );
};
